import { Col, ModalProps } from 'antd';
import moment, { MomentInput } from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { AccountStatus } from 'api/account';
import { Plans, SupportedPlans } from 'api/billing/types';
import { Button } from 'components/atoms/button';
import { useAccount, useIsAccountActive } from 'hooks/use-account';
import { useUser } from 'hooks/use-user';
import { usePlanName } from 'queries/billing';
import {
  Card,
  CardWrapper,
  ConfirmButton,
  ModalContent,
  StyledModal,
  TitleWrapper,
} from './styled-components';

const FeaturesList = {
  [`${SupportedPlans.Enterprise}_${SupportedPlans.Premium}`]: [
    'sso',
    'custom_email_domain',
    'rm_soft_allocations',
    'api_rate_limit',
  ],

  [`${SupportedPlans.Enterprise}_${SupportedPlans.Professional}`]: [
    'custom_domain',
    'sso',
    'custom_email_domain',
    'api',
    'rm',
    'rm_soft_allocations',
    'time_offs',
    'project_custom_fields',
    'task_custom_fields',
    'custom_project_status',
    'custom_task_status',
    'sf_integration',
    'jira_integration',
    'slack_integration',
    'custom_cards',
    'custom_charts',
    'multiple_dashboards',
    'status_update_template',
    'auto_publish_status_updates',
    'merge_templates',
    'dynamic_templates',
    'form_automation',
    'form_customisation',
    'proj_baseline',
    'proj_perf_report',
    'ppl_perf_report',
    'operation_insight_report',
  ],

  [`${SupportedPlans.Premium}_${SupportedPlans.Professional}`]: [
    'custom_domain',
    'rm',
    'time_offs',
    'project_custom_fields',
    'task_custom_fields',
    'custom_project_status',
    'custom_task_status',
    'sf_integration',
    'jira_integration',
    'slack_integration',
    'custom_cards',
    'custom_charts',
    'multiple_dashboards',
    'api',
    'status_update_template',
    'auto_publish_status_updates',
    'merge_templates',
    'dynamic_templates',
    'form_automation',
    'form_customisation',
    'proj_baseline',
    'proj_perf_report',
    'ppl_perf_report',
    'operation_insight_report',
  ],

  [`${SupportedPlans['Enterprise-V2']}_${SupportedPlans['Premium-V2']}`]: [
    'sso',
    'rm_soft_allocations',
    'api_rate_limit',
  ],

  [`${SupportedPlans['Enterprise-V2']}_${SupportedPlans.Standard}`]: [
    'sso',
    'custom_email_domain',
    'rm',
    'sf_integration',
    'workato_integration',
    'skills_based_resourcing',
    'capacity_management',
    'time_offs',
    'team_member_fields',
    'operation_insight_report',
    'auto_publish_status_updates',
    'rm_soft_allocations',
    'api_rate_limit',
  ],

  [`${SupportedPlans['Enterprise-V2']}_${SupportedPlans.Essentials}`]: [
    'custom_domain',
    'custom_email_domain',
    'sso',
    'rm',
    'rm_soft_allocations',
    'time_offs',
    'sf_integration',
    'task_custom_fields',
    'project_custom_fields',
    'dynamic_templates',
    'merge_templates',
    'custom_project_status',
    'custom_task_status',
    'custom_cards',
    'custom_charts',
    'multiple_dashboards',
    'workato_integration',
    'annotations_proofing',
    'forms',
    'slack_integration',
    'jira_integration',
    'api',
    'proj_baseline',
    'form_automation',
    'form_customisation',
    'proj_perf_report',
    'operation_insight_report',
    'auto_publish_status_updates',
  ],

  [`${SupportedPlans['Premium-V2']}_${SupportedPlans.Standard}`]: [
    'rm',
    'sf_integration',
    'workato_integration',
    'custom_email_domain',
    'skills_based_resourcing',
    'capacity_management',
    'time_offs',
    'team_member_fields',
    'operation_insight_report',
    'auto_publish_status_updates',
  ],

  [`${SupportedPlans['Premium-V2']}_${SupportedPlans.Essentials}`]: [
    'custom_domain',
    'custom_email_domain',
    'rm',
    'time_offs',
    'sf_integration',
    'task_custom_fields',
    'project_custom_fields',
    'dynamic_templates',
    'merge_templates',
    'custom_project_status',
    'custom_task_status',
    'custom_cards',
    'custom_charts',
    'multiple_dashboards',
    'workato_integration',
    'annotations_proofing',
    'forms',
    'slack_integration',
    'jira_integration',
    'api',
    'proj_baseline',
    'form_automation',
    'form_customisation',
    'proj_perf_report',
    'operation_insight_report',
    'auto_publish_status_updates',
  ],

  [`${SupportedPlans.Standard}_${SupportedPlans.Essentials}`]: [
    'custom_domain',
    'project_custom_fields',
    'task_custom_fields',
    'team_member_fields',
    'dynamic_templates',
    'merge_templates',
    'custom_project_status',
    'custom_task_status',
    'custom_cards',
    'custom_charts',
    'multiple_dashboards',
    'annotations_proofing',
    'forms',
    'slack_integration',
    'jira_integration',
    'api',
    'proj_baseline',
    'form_automation',
    'form_customisation',
    'proj_perf_report',
  ],
};

const FeatureCard = ({ featureName }: { featureName: string }) => {
  return (
    <Col span={8}>
      <Card>
        <div className="card-title">
          <FormattedMessage id={`billing.feature.card.title.${featureName}`} />
        </div>
        <div className="card-content">
          <FormattedMessage
            id={`billing.feature.card.content.${featureName}`}
          />
        </div>
      </Card>
    </Col>
  );
};

const ThingsToRememberCard = ({
  index,
  currentPlan,
  subscriptionEndDate,
}: {
  index: number;
  currentPlan: Plans;
  subscriptionEndDate: MomentInput;
}) => {
  const currentPlanName = usePlanName(currentPlan);

  return (
    <Col span={8}>
      <Card className="things-to-remember-card">
        <div className="card-title">{index}</div>
        <div className="card-content">
          <FormattedMessage
            id={`billing.downgrade.things.to.remember.card.content.${index}`}
            values={{
              br: <br />,
              currentPlan: currentPlanName,
              subscriptionEndDate:
                moment(subscriptionEndDate).format('DD MMM YYYY'),
            }}
          />
        </div>
      </Card>
    </Col>
  );
};

const StepOneTitle = ({ currentPlanName }: { currentPlanName: string }) => {
  return (
    <TitleWrapper>
      <FormattedMessage
        id="billing.downgrade.confirmation.title.step.one"
        values={{
          planName: currentPlanName,
        }}
      />
    </TitleWrapper>
  );
};

const StepTwoTitle = () => {
  return (
    <TitleWrapper>
      <FormattedMessage id="billing.downgrade.confirmation.title.step.two" />
    </TitleWrapper>
  );
};

const StepOneFooter = ({
  targetPlanName,
  onConfirm,
  onCancel,
}: {
  targetPlanName: string;
  onConfirm: (event) => void;
  onCancel: ModalProps['onCancel'];
}) => {
  return (
    <>
      <Button type="text" onClick={onCancel}>
        <FormattedMessage id="go.back" />
      </Button>
      <ConfirmButton type="primary" danger onClick={onConfirm}>
        <FormattedMessage
          id="billing.downgrade.to"
          values={{
            plan: targetPlanName,
          }}
        />
      </ConfirmButton>
    </>
  );
};

const StepTwoFooter = ({
  targetPlanName,
  isDowngrading,
  onOk,
  onCancel,
}: {
  targetPlanName: string;
  isDowngrading: boolean;
  onOk: ModalProps['onOk'];
  onCancel: () => void;
}) => {
  return (
    <>
      <Button type="text" onClick={onCancel}>
        <FormattedMessage id="go.back" />
      </Button>
      <ConfirmButton
        type="primary"
        danger
        onClick={onOk}
        loading={isDowngrading}
      >
        <FormattedMessage
          id="billing.downgrade.to"
          values={{
            plan: targetPlanName,
          }}
        />
      </ConfirmButton>
    </>
  );
};

const StepOneContent = ({
  currentPlan,
  targetPlan,
}: {
  currentPlan: Plans;
  targetPlan: Plans;
}) => {
  const {
    user: { firstName },
  } = useUser();
  const targetPlanName = usePlanName(targetPlan);

  const features =
    FeaturesList[
      `${SupportedPlans[currentPlan]}_${SupportedPlans[targetPlan]}`
    ];

  return (
    <>
      <b>
        <FormattedMessage
          id="billing.downgrade.content.heading.step.one"
          values={{
            userName: firstName,
            br: () => <br />,
            planName: targetPlanName,
          }}
        />
      </b>
      <CardWrapper gutter={[24, 24]}>
        {features.map((featureName) => (
          <FeatureCard featureName={featureName} />
        ))}
      </CardWrapper>
    </>
  );
};

const StepTwoContent = ({
  currentPlan,
  subscriptionEndDate,
}: {
  currentPlan: Plans;
  subscriptionEndDate: MomentInput;
}) => {
  return (
    <>
      <b>
        <FormattedMessage id="billing.downgrade.content.heading.step.two" />
      </b>
      <CardWrapper gutter={[40, 40]} style={{ marginTop: 'var(--spacing-08)' }}>
        {[1, 2, 3].map((value) => (
          <ThingsToRememberCard
            subscriptionEndDate={subscriptionEndDate}
            currentPlan={currentPlan}
            index={value}
          />
        ))}
      </CardWrapper>
    </>
  );
};

export const DowngradeConfirmation = ({
  currentPlan,
  targetPlan,
  subscriptionEndDate,
  isDowngrading,
  onOk,
  onCancel,
}: ModalProps & {
  currentPlan: Plans;
  targetPlan: Plans;
  subscriptionEndDate: MomentInput;
  isDowngrading: boolean;
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const { status: accountStatus } = useAccount();
  const isAccountActive = useIsAccountActive();
  const isTrial = useMemo(
    () => accountStatus === AccountStatus.Trial,
    [accountStatus],
  );
  const currentPlanName = usePlanName(currentPlan);
  const targetPlanName = usePlanName(targetPlan);

  const onStepOneConfirm = useCallback(
    (event) => {
      if (isTrial || !isAccountActive) {
        onOk?.(event);
      } else {
        setCurrentStep(2);
      }
    },
    [isAccountActive, isTrial, onOk],
  );

  return (
    <StyledModal
      width={1360}
      visible
      onCancel={onCancel}
      title={
        currentStep === 1 ? (
          <StepOneTitle currentPlanName={currentPlanName} />
        ) : (
          <StepTwoTitle />
        )
      }
      footer={
        currentStep === 1 ? (
          <StepOneFooter
            targetPlanName={targetPlanName}
            onConfirm={onStepOneConfirm}
            onCancel={onCancel}
          />
        ) : (
          <StepTwoFooter
            isDowngrading={isDowngrading}
            targetPlanName={targetPlanName}
            onOk={onOk}
            onCancel={() => setCurrentStep(1)}
          />
        )
      }
    >
      <ModalContent>
        {currentStep === 1 ? (
          <StepOneContent currentPlan={currentPlan} targetPlan={targetPlan} />
        ) : (
          <StepTwoContent
            currentPlan={currentPlan}
            subscriptionEndDate={subscriptionEndDate}
          />
        )}
      </ModalContent>
    </StyledModal>
  );
};
